import { useState, type RefObject } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { useTenantSettings } from '../../contexts/TenantSettingsContext';
import { wrapWithTenantBrand, a4ContentWidthPx } from '../../lib/pdf-brand';

/**
 * Couple de boutons "Excel" et "PDF" pour les pages de reporting.
 *
 *  - Excel : appelle `apiUrl` (ex. `/reporting/dashboard.xlsx`) en blob, puis
 *    déclenche le téléchargement client.
 *  - PDF   : appelle `html2pdf.js` (chargé en dynamique pour ne pas alourdir
 *    le bundle initial) sur le nœud DOM ciblé par `targetRef`. Options : A4
 *    paysage, marges 10 mm.
 */
export interface ExportButtonsProps {
  /** URL backend pour générer le Excel (.xlsx). */
  excelUrl: string;
  /** Paramètres query string injectés dans `excelUrl`. */
  excelParams?: URLSearchParams;
  /** Suffixe ajouté au nom du fichier exporté (excel + pdf). */
  filename: string;
  /** Référence au DOM à imprimer en PDF. */
  targetRef: RefObject<HTMLElement | null>;
  /** Désactive le bouton "PDF" si pas de cible. */
  pdfDisabled?: boolean;
}

export function ExportButtons({
  excelUrl,
  excelParams,
  filename,
  targetRef,
  pdfDisabled = false,
}: ExportButtonsProps) {
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { tenant, settings } = useTenantSettings();

  async function handleExcel() {
    setExcelLoading(true);
    setErrorMessage(null);
    try {
      const qs =
        excelParams && [...excelParams.keys()].length > 0 ? `?${excelParams.toString()}` : '';
      const res = await api.get(`${excelUrl}${qs}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, "Erreur lors de l'export Excel"));
    } finally {
      setExcelLoading(false);
    }
  }

  async function handlePdf() {
    if (!targetRef.current) return;
    setPdfLoading(true);
    setErrorMessage(null);
    const wrap = wrapWithTenantBrand(
      targetRef.current,
      {
        name: tenant?.nom,
        logoUrl: tenant?.logo_signed_url ?? tenant?.logo_url,
        primaryColor: settings?.branding?.primary_color,
      },
      // Reporting exporté en A4 paysage, marge 10mm (cf. options jsPDF ci-dessous).
      { widthPx: a4ContentWidthPx('landscape', 10) },
    );
    try {
      // Import dynamique pour éviter d'alourdir le bundle initial.
      const mod = await import('html2pdf.js');
      const html2pdf = mod.default;
      await html2pdf()
        .from(wrap.target)
        .set({
          margin: 10,
          filename: `${filename}-${new Date().toISOString().slice(0, 10)}.pdf`,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        })
        .save();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, "Erreur lors de l'export PDF"));
    } finally {
      wrap.cleanup();
      setPdfLoading(false);
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleExcel}
        disabled={excelLoading}
        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
        title="Télécharger en Excel (.xlsx)"
      >
        {excelLoading ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin text-green-600" />
        ) : (
          <FileSpreadsheet className="w-4 h-4 mr-1.5 text-green-600" />
        )}
        Excel
      </button>
      <button
        type="button"
        onClick={handlePdf}
        disabled={pdfLoading || pdfDisabled}
        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
        title="Télécharger en PDF"
      >
        {pdfLoading ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin text-red-600" />
        ) : (
          <FileText className="w-4 h-4 mr-1.5 text-red-600" />
        )}
        PDF
      </button>
      {errorMessage && (
        <span className="inline-flex items-center text-xs text-red-600 ml-2">
          <Download className="w-3 h-3 mr-1" />
          {errorMessage}
        </span>
      )}
    </div>
  );
}
